import { useState } from 'react'
import {
  Phone,
  CheckCircle,
  XCircle,
  PhoneMissed,
  Clock,
  Ban,
  SkipForward,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useCampaignContacts } from '../../api/campaigns'
import { useDispositionLabel } from '../../i18n/dispositionLabels'
import { formatPhone, timeAgo, cn } from '../../lib/utils'

const PAGE_SIZE = 10

const DISPOSITION_META: Record<string, { icon: typeof CheckCircle; color: string }> = {
  Interested: { icon: CheckCircle, color: 'text-success' },
  'Not Interested': { icon: XCircle, color: 'text-error' },
  'No Answer': { icon: PhoneMissed, color: 'text-warning' },
  Callback: { icon: Clock, color: 'text-info' },
  DNC: { icon: Ban, color: 'text-text-muted' },
}

interface CampaignCallHistoryProps {
  listId: number
}

function HistoryItem({ contact, isSkipped }: {
  contact: { id: number; name: string; phone: string; status: string; disposition: string | null; called_at: number | null }
  isSkipped: boolean
}) {
  const { t } = useTranslation('campaigns')
  const meta = contact.disposition ? DISPOSITION_META[contact.disposition] : null
  const Icon = isSkipped ? SkipForward : meta?.icon ?? Phone
  const iconColor = isSkipped ? 'text-text-muted' : meta?.color ?? 'text-text-muted'
  const dispositionLabel = useDispositionLabel(contact.disposition ?? '')

  return (
    <li className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
      <div
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-elevated',
          iconColor,
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text-primary">
          {contact.name || t('runner.unknown')}
        </p>
        <p className="flex items-center gap-1 text-xs text-text-muted">
          <Phone className="h-3 w-3" />
          {formatPhone(contact.phone)}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <span
          className={cn(
            'inline-block rounded-full px-2 py-0.5 text-[11px] font-medium',
            isSkipped
              ? 'bg-surface-elevated text-text-muted'
              : meta
                ? `${meta.color} bg-surface-elevated`
                : 'bg-surface-elevated text-text-muted',
          )}
        >
          {isSkipped ? t('runner.skipped') : contact.disposition ? dispositionLabel : t('runner.called')}
        </span>
        {contact.called_at && (
          <p className="mt-0.5 text-[10px] text-text-muted">
            {timeAgo(contact.called_at)}
          </p>
        )}
      </div>
    </li>
  )
}

export function CampaignCallHistory({ listId }: CampaignCallHistoryProps) {
  const { t } = useTranslation('campaigns')
  const [page, setPage] = useState(0)

  const { data } = useCampaignContacts(listId, {
    status: 'called',
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  })

  const { data: skippedData } = useCampaignContacts(listId, {
    status: 'skipped',
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  })

  const calledContacts = data?.contacts ?? []
  const skippedContacts = skippedData?.contacts ?? []
  const totalCalled = data?.total ?? 0
  const totalSkipped = skippedData?.total ?? 0
  const totalDone = totalCalled + totalSkipped

  // Merge, sort by most recent, then paginate the merged result
  const merged = [...calledContacts, ...skippedContacts]
    .sort((a, b) => (b.called_at ?? b.created_at) - (a.called_at ?? a.created_at))
    .slice(0, PAGE_SIZE)

  const totalPages = Math.max(1, Math.ceil(totalDone / PAGE_SIZE))

  if (totalDone === 0) return null

  return (
    <div className="rounded-lg border border-border bg-surface-card p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">{t('runner.callHistory')}</h3>
        <span className="text-xs text-text-muted">{t('runner.completed', { count: totalDone })}</span>
      </div>

      <ul className="mt-3 divide-y divide-border">
        {merged.map((contact) => (
          <HistoryItem
            key={contact.id}
            contact={contact}
            isSkipped={contact.status === 'skipped'}
          />
        ))}
      </ul>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
              page === 0
                ? 'cursor-not-allowed text-text-muted/40'
                : 'text-text-secondary hover:bg-surface-elevated',
            )}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            {t('runner.previous')}
          </button>
          <span className="text-xs text-text-muted">
            {t('runner.page', { current: page + 1, total: totalPages })}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
              page >= totalPages - 1
                ? 'cursor-not-allowed text-text-muted/40'
                : 'text-text-secondary hover:bg-surface-elevated',
            )}
          >
            {t('runner.next')}
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}
